import { KycApiClient, KycRecord, KycProvider, CreateKycRequest, CreateKycResponse } from "../clients/kycApiClient";

export class KycService {

  /**
   * Create a new KYC for a company
   * @param companyId Company ID
   * @param provider KYC provider (defaults to 'openpayments')
   * @param type KYC type (defaults to 'payment')
   * @param countryCode ISO 3166-1 alpha-2 country code (defaults to 'SE')
   * @returns Promise with KYC URL
   */
  async createKyc(
    companyId: string,
    provider: KycProvider = 'openpayments',
    type: 'payment' | 'onboarding' = 'payment',
    countryCode: string = 'SE',
  ): Promise<CreateKycResponse> {
    const request: CreateKycRequest = { provider, type, countryCode };
    const response = await KycApiClient.createKyc(companyId, request);

    if (response?.url) {
      console.log(`KYC created for company ${companyId} with URL: ${response.url}`);
      return response;
    }
    throw new Error("Failed to create KYC");
  }

  /**
   * Get a specific KYC by ID
   * @param companyId Company ID
   * @param kycId KYC ID
   * @returns Promise with the KYC record
   */
  async getKyc(companyId: string, kycId: string): Promise<KycRecord> {
    const kycRecord = await KycApiClient.getKyc(companyId, kycId);

    if (kycRecord) {
      console.log(`KYC retrieved with ID ${kycRecord.id} for company ${companyId}`);
      return kycRecord;
    }
    throw new Error("Failed to get KYC");
  }

  /**
   * List KYCs for a company
   * @param companyId Company ID
   * @param latest Whether to get only the latest KYC
   * @returns Promise with array of KYC records
   */
  async listKycs(companyId: string): Promise<KycRecord[]> {
    const kycRecords = await KycApiClient.listKycs(companyId);

    console.log(`Retrieved ${kycRecords.length} KYC records for company ${companyId}`);
    return kycRecords;
  }

  /**
   * Get the latest KYC for a company by provider and type
   * @param companyId Company ID
   * @param provider KYC provider
   * @param type KYC type
   * @returns Promise with the latest KYC record or null
   */
  async getLatestKyc(
    companyId: string,
    provider: KycProvider = 'openpayments',
    type: 'payment' | 'onboarding' = 'payment'
  ): Promise<KycRecord | null> {
    const kycRecord = await KycApiClient.getLatestKyc(companyId, provider, type);

    if (kycRecord) {
      console.log(`Latest KYC retrieved with ID ${kycRecord.id} for company ${companyId}`);
    } else {
      console.log(`No KYC found for company ${companyId} with provider ${provider} and type ${type}`);
    }

    return kycRecord;
  }

  /**
   * Check if a company has valid KYC for payments
   * @param companyId Company ID
   * @param provider KYC provider
   * @param type KYC type
   * @returns Promise with boolean indicating if KYC is valid
   */
  async hasValidKyc(
    companyId: string,
    provider: KycProvider = 'openpayments',
    type: 'payment' | 'onboarding' = 'payment'
  ): Promise<boolean> {
    try {
      const latestKyc = await this.getLatestKyc(companyId, provider, type);
      if (!latestKyc) {
        return false;
      }

      const isValid = KycApiClient.isKycValid(latestKyc);
      console.log(`KYC validity check for company ${companyId}: ${isValid}`);
      return isValid;
    } catch (error) {
      console.error('Error checking KYC validity:', error);
      return false;
    }
  }

  /**
   * Check if a KYC needs refresh
   * @param kyc KYC record
   * @returns Whether the KYC needs refresh
   */
  needsRefresh(kyc: KycRecord): boolean {
    return KycApiClient.needsRefresh(kyc);
  }

  /**
   * Check if a KYC is valid (not expired and completed)
   * @param kyc KYC record
   * @returns Whether the KYC is valid
   */
  isKycValid(kyc: KycRecord): boolean {
    return KycApiClient.isKycValid(kyc);
  }

  /**
   * Get KYC completion status for display purposes
   * @param companyId Company ID
   * @param provider KYC provider
   * @param type KYC type
   * @returns Promise with KYC status information
   */
  async getKycStatus(
    companyId: string,
    provider: KycProvider = 'openpayments',
    type: 'payment' | 'onboarding' = 'payment'
  ): Promise<{
    hasKyc: boolean;
    isValid: boolean;
    needsRefresh: boolean;
    status?: 'pending' | 'completed' | 'failed' | 'invalid';
    kycUrl?: string;
    kyc?: KycRecord;
  }> {
    try {
      const latestKyc = await this.getLatestKyc(companyId, provider, type);

      if (!latestKyc) {
        return {
          hasKyc: false,
          isValid: false,
          needsRefresh: false
        };
      }

      const isValid = this.isKycValid(latestKyc);
      const needsRefresh = this.needsRefresh(latestKyc);
      const kycUrl = latestKyc.extra_data?.kyc_url;

      return {
        hasKyc: true,
        isValid,
        needsRefresh,
        status: latestKyc.status,
        kycUrl,
        kyc: latestKyc
      };
    } catch (error) {
      console.error('Error getting KYC status:', error);
      return {
        hasKyc: false,
        isValid: false,
        needsRefresh: false
      };
    }
  }

  /**
   * Validate that a company has valid KYC before proceeding with payment operations
   * @param companyId Company ID
   * @throws Error if KYC is not valid
   */
  async validateKycForPayment(companyId: string): Promise<void> {
    const hasValid = await this.hasValidKyc(companyId);
    if (!hasValid) {
      throw new Error('Valid KYC required for payment processing. Please complete KYC verification.');
    }
  }
}

export default KycService;